import {
  ATTRIBUTE_TAB_NAV_SETTINGS,
  ATTRIBUTE_TAB_CONTENT_SETTINGS,
} from '@helpers/slider-utils.js';
import {
  RangeControl,
  ToggleControl,
  __experimentalNumberControl as NumberControl,
} from '@wordpress/components';

const {
  i18n: { __ },
  components: { PanelBody },
  element: { useState, useEffect },
} = wp;

/**
 *
 * @param {Object}   props
 * @param {Object}   props.sliderMediaBreakPoint        { type: 'sm', breakPoint: 576, slug: 'mobile', label: 'Mobile' }
 * @param {Object}   props.sliderTabNavSettings         Slider nav attributes of a specific device (mobile, tablet, tablet-landscape, desktop)
 * @param {Object}   props.sliderTabContentSettings     Slider content attributes of a specific device (mobile, tablet, tablet-landscape, desktop)
 * @param {Array}    props.slides                       Number of tab nav in the editor
 * @param {Function} props.setSliderSettingAttribute    Function for change attributes
 * @param {Function} props.removeSliderSettingAttribute Function for delete attributes
 * @return {HTMLElement} Returns the HTML element
 */
const CommonFields = (props) => {
  const {
    sliderMediaBreakPoint,
    sliderTabNavSettings,
    sliderTabContentSettings,
    slides,
    setSliderSettingAttribute,
    removeSliderSettingAttribute,
  } = props;
  const { type, label, slug } = sliderMediaBreakPoint;
  const [sliderAutoplay, setSliderAutoplay] = useState(false);
  const DEFAULT_AUTOPLAY_SPEED = 2500;
  const sliderLength = slides.length;

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    sliderTabNavSettings.autoplay && sliderTabContentSettings.autoplay
      ? setSliderAutoplay(true)
      : setSliderAutoplay(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valueFormatter = (value) => {
    let castedValue;
    if (typeof value === 'string') {
      castedValue = parseInt(value);
    } else if (typeof value === 'number') {
      castedValue = value;
    } else {
      castedValue = NaN;
    }
    return !isNaN(castedValue) ? (castedValue + 1).toString() : '';
  };

  return (
    <PanelBody title={label} initialOpen={false} key={type}>
      <ToggleControl
        label={__('Infinite', 'b3rg')}
        checked={sliderTabNavSettings.loop && sliderTabContentSettings.loop}
        onChange={(value) => {
          const settingKey = 'loop';
          setSliderSettingAttribute([
            {
              attribute: ATTRIBUTE_TAB_NAV_SETTINGS,
              layout: slug,
              property: settingKey,
              childProperty: '',
              value,
            },
            {
              attribute: ATTRIBUTE_TAB_CONTENT_SETTINGS,
              layout: slug,
              property: settingKey,
              childProperty: '',
              value,
            },
          ]);
        }}
      />
      <ToggleControl
        label={__('Autoplay', 'b3rg')}
        checked={sliderAutoplay}
        onChange={(value) => {
          setSliderAutoplay(value);
          const settingKey = 'autoplay';
          if (value === true) {
            setSliderSettingAttribute([
              {
                attribute: ATTRIBUTE_TAB_NAV_SETTINGS,
                layout: slug,
                property: settingKey,
                childProperty: 'delay',
                value: DEFAULT_AUTOPLAY_SPEED,
              },
              {
                attribute: ATTRIBUTE_TAB_NAV_SETTINGS,
                layout: slug,
                property: settingKey,
                childProperty: 'disableOnInteraction',
                value: false,
              },
              {
                attribute: ATTRIBUTE_TAB_CONTENT_SETTINGS,
                layout: slug,
                property: settingKey,
                childProperty: 'delay',
                value: DEFAULT_AUTOPLAY_SPEED,
              },
              {
                attribute: ATTRIBUTE_TAB_CONTENT_SETTINGS,
                layout: slug,
                property: settingKey,
                childProperty: 'disableOnInteraction',
                value: false,
              },
            ]);
          } else {
            removeSliderSettingAttribute([
              {
                attribute: ATTRIBUTE_TAB_NAV_SETTINGS,
                layout: slug,
                attributeKey: settingKey,
                attributeChildKey: '',
              },
              {
                attribute: ATTRIBUTE_TAB_CONTENT_SETTINGS,
                layout: slug,
                attributeKey: settingKey,
                attributeChildKey: '',
              },
            ]);
          }
        }}
      />
      {sliderAutoplay && (
        <RangeControl
          label={__('Autoplay Speed', 'b3rg')}
          min={100}
          max={10000}
          value={
            (sliderTabNavSettings.autoplay?.delay +
              sliderTabContentSettings.autoplay?.delay) /
            2
          }
          step={1}
          onChange={(value) => {
            const settingKey = 'autoplay';
            const settingChildKey = 'delay';
            setSliderSettingAttribute([
              {
                attribute: ATTRIBUTE_TAB_NAV_SETTINGS,
                layout: slug,
                property: settingKey,
                childProperty: settingChildKey,
                value,
              },
              {
                attribute: ATTRIBUTE_TAB_CONTENT_SETTINGS,
                layout: slug,
                property: settingKey,
                childProperty: settingChildKey,
                value,
              },
            ]);
          }}
        />
      )}
      <NumberControl
        label={__('Initial Slide', 'b3rg')}
        isShiftStepEnabled={true}
        onChange={(value) => {
          const settingKey = 'initialSlide';
          const initialSlide = parseInt(value) - 1;
          setSliderSettingAttribute([
            {
              attribute: ATTRIBUTE_TAB_NAV_SETTINGS,
              layout: slug,
              property: settingKey,
              childProperty: '',
              value: initialSlide || undefined,
            },
            {
              attribute: ATTRIBUTE_TAB_CONTENT_SETTINGS,
              layout: slug,
              property: settingKey,
              childProperty: '',
              value: initialSlide || undefined,
            },
          ]);
        }}
        max={sliderLength}
        min={1}
        step={1}
        shiftStep={1}
        value={valueFormatter(sliderTabNavSettings.initialSlide)}
      />
    </PanelBody>
  );
};

export default CommonFields;
